import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    contentChild,
    ElementRef,
    forwardRef,
    inject,
    input,
    InputSignal,
    model,
    ModelSignal,
    output,
    OutputEmitterRef,
    Signal,
    viewChild
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {FormValueControl} from '@angular/forms/signals';
import {TranslatePipe} from '@ngx-translate/core';
import {AppButtonTypeEnum} from '../../../enums/button/app-button-type.enum';
import {AppInputColorEnum} from '../../../enums/input/input-color.enum';
import {ICONS} from '../../../constants/icons/icons.constants';
import {ButtonComponent} from '../../buttons/button/button.component';
import {STATIC_CONFIGS} from '../../../../configs/static.config';
import {ContentPrefixDirective} from '../../../directives/content-prefix.directive';
import {ContentSuffixDirective} from '../../../directives/content-suffix.directive';
import {AppTextInputTypeEnum} from '../../../enums/app-text-input-type.enum';
import {TooltipPositionEnum} from '../../../enums/tooltip/tooltip-position.enum';

@Component({
    selector: 'app-text-input',
    imports: [
        ButtonComponent,
        TranslatePipe
    ],
    templateUrl: './text-input.component.html',
    styleUrl: './text-input.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TextInputComponent),
            multi: true
        }
    ],
    host: {
        '[class.color-default]': 'inputColor() === AppInputColorEnum.DEFAULT',
        '[class.color-login]': 'inputColor() === AppInputColorEnum.LOGIN',
    }
})
export class TextInputComponent implements ControlValueAccessor, FormValueControl<string> {
    public value: ModelSignal<string> = model<string>('');
    public blurred: OutputEmitterRef<void> = output();
    public focused: OutputEmitterRef<void> = output();
    public hasClearButton: InputSignal<boolean> = input<boolean>(true);
    public shouldFocusAfterClearButton: InputSignal<boolean> = input<boolean>(true);
    public placeholder: InputSignal<string> = input<string>('');
    public type: InputSignal<AppTextInputTypeEnum> = input<AppTextInputTypeEnum>(AppTextInputTypeEnum.TEXT);
    public autocomplete: InputSignal<string | null> = input<string | null>('off');
    public ariaLabel: InputSignal<string | null> = input<string | null>(null);
    public inputColor: InputSignal<AppInputColorEnum> = input<AppInputColorEnum>(AppInputColorEnum.DEFAULT);
    public maxInputLength: InputSignal<number | undefined> = input<number | undefined>(STATIC_CONFIGS.maxInputCharSize);
    public minInputLength: InputSignal<number | undefined> = input<number | undefined>(undefined);
    public qaTag: InputSignal<string> = input<string>('text-input');
    public inputId: InputSignal<string> = input.required<string>();
    protected readonly AppInputColorEnum = AppInputColorEnum;
    protected readonly AppButtonTypeEnum = AppButtonTypeEnum;
    protected readonly TooltipPositionEnum = TooltipPositionEnum;
    protected readonly prefixContent: Signal<ContentPrefixDirective | undefined> = contentChild(ContentPrefixDirective);
    protected readonly suffixContent: Signal<ContentSuffixDirective | undefined> = contentChild(ContentSuffixDirective);
    protected effectiveMaxLength: Signal<number>;
    protected readonly ICONS = ICONS;
    protected isDisabled: boolean = false;
    protected inputElement = viewChild('inputElement', {
        read: ElementRef
    });
    private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private onChange: (value: string) => void;
    private onTouched: () => void;

    public constructor() {
        this.effectiveMaxLength = computed(() => {
            return Math.min(this.maxInputLength() ?? STATIC_CONFIGS.maxInputCharSize, STATIC_CONFIGS.maxInputCharSize);
        });

        this.onChange = (): void => {
        };

        this.onTouched = (): void => {
        };
    }

    public writeValue(value: string): void {
        this.value.set(value ?? '');
        this.changeDetectorRef.markForCheck();
    }

    public registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
        this.changeDetectorRef.markForCheck();
    }

    public blur(): void {
        this.inputElement()
            ?.nativeElement
            .blur();
    }

    public focus(): void {
        this.inputElement()
            ?.nativeElement
            .focus();
    }

    protected onInputChange(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.value.set(value);
        this.onChange(value);
    }

    protected onInputFocus(): void {
        this.focused.emit();
    }

    protected onInputBlur(): void {
        this.onTouched();
        this.blurred.emit();
    }

    protected onClear(): void {
        this.value.set('');
        this.onChange('');
        this.onTouched();
        if (this.shouldFocusAfterClearButton()) {
            this.focus();
        }
    }
}
